import {Money} from "@/graphql/generated/graphql";
import {graphql} from "@/graphql/generated";

export const moneyFragment = graphql(`fragment MoneyFragment on Money {
    amount,
    currency
}`)

export const isMoney = (value: any): value is Money => {
    return value && typeof value === 'object' && 'amount' in value && 'currency' in value;
}

const validate = (m: Money) => {
    if (m.currency !== 'EUR' || !isMoney(m)) {
        throw new Error("malformed or unsupported Money")
    }
}

export const toFloat = (m: Money) : number => {
    validate(m)
    return m.amount / 100
}
export const fromFloat = (f: number, currency: string = 'EUR') : Money => {
    return {
        amount: Math.round(f * 100),
        currency
    }
}

export const Zero = fromFloat(0)

const moneyDefaults = {
    style: "currency",
    currency: "EUR",
    signDisplay:'negative',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
} as Intl.NumberFormatOptions
const largeMoneyDefaults = {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
} as Intl.NumberFormatOptions
const localesDefault = "de-DE"

export const formatLargeMoney = (m: Money, numberFormatOptions:Intl.NumberFormatOptions = largeMoneyDefaults, locales = localesDefault) => formatMoney(m, {...largeMoneyDefaults, ...numberFormatOptions}, locales)
export const formatMoney = (m: Money, numberFormatOptions:Intl.NumberFormatOptions = moneyDefaults, locales = localesDefault) => formatFloat(toFloat(m), {...moneyDefaults, ...numberFormatOptions}, locales)

export const formatLargeFloat = (amount: number, numberFormatOptions:Intl.NumberFormatOptions = largeMoneyDefaults, locales = localesDefault) => formatFloat(amount, {...largeMoneyDefaults, ...numberFormatOptions}, locales)
export const formatFloat = (amount: number, numberFormatOptions:Intl.NumberFormatOptions = moneyDefaults, locales = localesDefault) => Intl.NumberFormat(
    locales,
    {
        ...moneyDefaults,
        ...numberFormatOptions
    })
        .format(amount)
    // const eur = Math.floor(m.amount / 100)
    // const cents = m.amount - (eur * 100)
    // return `${eur},${cents}€`


export const add = (m: Money, n: Money|number) => {
    validate(m)
    let amount = m.amount

    if (typeof n === "number") {
        amount += n
    } else {
        validate(n)
        amount += n.amount
    }

    return {
        amount,
        currency: m.currency,
    }
}

export const sub = (m: Money, n: Money|number) => {
    validate(m)
    let amount = m.amount

    if (typeof n === "number") {
        amount -= n
    } else {
        validate(n)
        amount -= n.amount
    }

    return {
        amount,
        currency: m.currency,
    }
}

export const abs = (m: Money) => {
    validate(m)
    let amount = m.amount

    return {
        amount: amount < 0 ? - amount : amount,
        currency: m.currency,
    }
}

export const lt = (m1: Money, m2: Money) => {
    validate(m1)
    validate(m2)
    return m1.amount < m2.amount
}
export const le = (m1: Money, m2: Money) => {
    validate(m1)
    validate(m2)
    return m1.amount <= m2.amount
}
export const gt = (m1: Money, m2: Money) => {
    validate(m1)
    validate(m2)
    return m1.amount > m2.amount
}
export const ge = (m1: Money, m2: Money) => {
    validate(m1)
    validate(m2)
    return m1.amount >= m2.amount
}

export const multiply = (m: Money, n: number) => {
    validate(m)
    let amount = toFloat(m)

    return fromFloat(amount * n, m.currency)
}

export default {
    isMoney,
    formatMoney,
    formatLargeMoney,
    formatFloat,
    toFloat,
    add,
    sub,
    abs,
    lt,
    le,
    gt,
    ge,
    multiply,
    Zero
}
